import { formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import { Text } from "@clipboard-health/ui-components";
import BoltIcon from "@mui/icons-material/Bolt";
import { Divider, Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { parseISO } from "date-fns";

import { type ShiftBlockWithMappedDetails } from "../api/useFetchPaginatedShiftBlocks";
import { getHumanReadableShiftDuration } from "../utils";
import { BookedShiftBlockActions } from "./BookedShiftBlockActions";
import { LunchBreakInformationIconAndDialog } from "./LunchBreakInformationIconAndDialog";

interface ShiftBlockShiftDetailsProps {
  shiftBlockWithDetails: ShiftBlockWithMappedDetails;
  agentTimezone: string;
  mandatoryBreakDurationInMinutes: number;
}
export function ShiftBlockShiftDetails(props: ShiftBlockShiftDetailsProps) {
  const { shiftBlockWithDetails, agentTimezone, mandatoryBreakDurationInMinutes } = props;
  const {
    shifts,
    isShiftBlockAssigned,
    facility: { name: facilityName },
  } = shiftBlockWithDetails;

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Text variant="h3">{isShiftBlockAssigned ? "Booked Block Shifts" : "Block Shifts"}</Text>
        <BookedShiftBlockActions shiftBlockWithDetails={shiftBlockWithDetails} />
      </Stack>

      {shifts.map((shift, index) => {
        return (
          <Stack key={shift.id} spacing={1}>
            <Stack>
              <Text>
                {formatShortDateWithWeekday(parseISO(shift.start), { timeZone: agentTimezone })}
              </Text>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Text>
                  {formatTime(parseISO(shift.start), { timeZone: agentTimezone })} -{" "}
                  {formatTime(parseISO(shift.end), { timeZone: agentTimezone })}
                </Text>
                <Stack direction="row" alignItems="center">
                  <Text
                    sx={{
                      opacity: 0.6,
                    }}
                  >
                    {getHumanReadableShiftDuration({
                      shiftDuration: shift.time,
                      withLunchBreak: false,
                      mandatoryBreakDurationInMinutes,
                    })}
                  </Text>
                  <LunchBreakInformationIconAndDialog
                    shiftDetails={{
                      facilityName,
                      shiftTime: shift.time,
                      mandatoryBreakDurationInMinutes,
                    }}
                  />
                </Stack>
              </Stack>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                justifyContent="space-between"
              >
                <Text
                  sx={{
                    color: (theme) => theme.palette.success.main,
                    fontWeight: 600,
                  }}
                  aria-label="Shift Price"
                >
                  {formatDollarsAsUsd(shift.offer?.pay?.value ?? shift.pay, {
                    minimumFractionDigits: 2,
                  })}
                  /hr
                </Text>

                {shift.isInstantPay && (
                  <Stack direction="row" spacing={0.25}>
                    <BoltIcon sx={{ color: (theme) => theme.palette.success.main }} />
                    <Text sx={{ color: (theme) => theme.palette.success.main, fontWeight: 600 }}>
                      InstantPay
                    </Text>
                  </Stack>
                )}
              </Stack>
            </Stack>
            {index !== shifts.length - 1 && <Divider />}
          </Stack>
        );
      })}
    </>
  );
}
